// Scene-agnostic splat-mesh updater — the CPU-lift present path for OGS splats.
// It drives a three `GaussianSplat` inside an EXTERNAL scene, i.e. the host
// React-Three-Fiber `<Canvas>` scene (OGSModel.jsx).
//
// WHAT THE SPARK REMOVAL TOOK OUT OF HERE, and why none of it had a job left:
//
//   The PACKED path. `ensurePackedTarget` / `commitPacked` existed to hand a
//   GPU lifter the array Spark uploads from, so a frame's words made exactly one
//   CPU copy. There is no Spark to upload to, and every GPU lifter now writes
//   three's four buffers DIRECTLY (`setThreeTargets`, see splatOverlayMount) with
//   no readback at all. A readback present would be strictly slower than the path
//   that replaced it, so it is deleted rather than kept as a fallback.
//
//   The PLY REBUILD. The SoA path used to pack a 3DGS PLY per update and hand it
//   to `new SplatMesh({fileBytes})` — a parse per frame, because that was the
//   only way into Spark's representation. three takes the arrays, so the mesh is
//   built ONCE at capacity and its attributes are written in place.
//
// WHAT IS LEFT is the one thing that still needs a CPU lift: a device with no
// WebGPU, where `GaussianLifter` produces SoA Gaussians and nothing can write a
// GPUBuffer. That still renders, because `WebGPURenderer` falls back to its own
// WebGL2 backend and `GaussianSplat` draws there (PBO-backed buffers, a CPU
// counting sort). Slow, and correct, and the only reason the WebGL backend is
// kept at all.

import * as THREE from "three";
import { GaussianSplat } from "./three/threeSplatDeps.js";
import type { Gaussians } from "../inference/lift";

/** Place a freshly built splat in the scene: uniform cm→m scale, the bundle's
 *  world_rotation, then the scene-space offset — three.js composes them as
 *  T·R·S, i.e. `world = rotation·(local·scale) + offset`, which is the placement
 *  the plücker camera inverts (inference/plucker.ts toLocalOrigin/toLocalRotation). */
export function applyMeshPlacement(
  mesh: {
    scale: { setScalar: (v: number) => void };
    position: { set: (x: number, y: number, z: number) => void };
    quaternion: { copy: (q: THREE.Quaternion) => unknown };
  },
  meshScale: number,
  meshOffset: readonly [number, number, number],
  meshRotation: THREE.Quaternion | null = null,
): void {
  if (meshScale !== 1) mesh.scale.setScalar(meshScale);
  if (meshRotation) mesh.quaternion.copy(meshRotation);
  const [ox, oy, oz] = meshOffset;
  if (ox !== 0 || oy !== 0 || oz !== 0) mesh.position.set(ox, oy, oz);
}

/** Covariance for one Gaussian from its scale and rotation: (R·S)(R·S)ᵀ, the
 *  six upper-triangle terms in the order three's `covariance` attribute wants
 *  (xx, xy, xz, yy, yz, zz). The GPU lift computes the same thing in
 *  `liftThreeVariant`'s emit block; this is the CPU twin, and the two must agree
 *  — a mismatch renders as splats with the wrong long axis, which reads as a
 *  soft, smeared avatar rather than as an error. */
export function writeCovariance(
  out: Float32Array, at: number,
  sx: number, sy: number, sz: number,
  qw: number, qx: number, qy: number, qz: number,
): void {
  const r00 = 1 - 2 * (qy * qy + qz * qz);
  const r01 = 2 * (qx * qy - qw * qz);
  const r02 = 2 * (qx * qz + qw * qy);
  const r10 = 2 * (qx * qy + qw * qz);
  const r11 = 1 - 2 * (qx * qx + qz * qz);
  const r12 = 2 * (qy * qz - qw * qx);
  const r20 = 2 * (qx * qz - qw * qy);
  const r21 = 2 * (qy * qz + qw * qx);
  const r22 = 1 - 2 * (qx * qx + qy * qy);
  const m00 = r00 * sx, m01 = r01 * sy, m02 = r02 * sz;
  const m10 = r10 * sx, m11 = r11 * sy, m12 = r12 * sz;
  const m20 = r20 * sx, m21 = r21 * sy, m22 = r22 * sz;
  out[at] = m00 * m00 + m01 * m01 + m02 * m02;
  out[at + 1] = m00 * m10 + m01 * m11 + m02 * m12;
  out[at + 2] = m00 * m20 + m01 * m21 + m02 * m22;
  out[at + 3] = m10 * m10 + m11 * m11 + m12 * m12;
  out[at + 4] = m10 * m20 + m11 * m21 + m12 * m22;
  out[at + 5] = m20 * m20 + m21 * m21 + m22 * m22;
}

const sigmoid = (x: number) => 1 / (1 + Math.exp(-x));
const byte = (v: number) => (v <= 0 ? 0 : v >= 1 ? 255 : Math.round(v * 255));

/** SoA Gaussians -> the three attributes a `GaussianSplat` reads, written into
 *  arrays the caller owns. Exported because the parity harness builds the same
 *  geometry from the same lift output: a second transcription of this is exactly
 *  how a harness comes to disagree with what ships. */
export function writeSplatAttributes(
  g: Gaussians, count: number,
  centre: Float32Array, cov: Float32Array, colour: Uint8ClampedArray,
): void {
  const { positions, scales, rotations, colors, opacities } = g;
  for (let i = 0; i < count; i++) {
    centre[i * 3] = positions[i * 3];
    centre[i * 3 + 1] = positions[i * 3 + 1];
    centre[i * 3 + 2] = positions[i * 3 + 2];
    // The lifter stores rotation as (w,x,y,z), the same order the GPU emit block
    // reads it in.
    writeCovariance(cov, i * 6,
      scales[i * 3], scales[i * 3 + 1], scales[i * 3 + 2],
      rotations[i * 4], rotations[i * 4 + 1], rotations[i * 4 + 2], rotations[i * 4 + 3]);
    colour[i * 4] = byte(colors[i * 3]);
    colour[i * 4 + 1] = byte(colors[i * 3 + 1]);
    colour[i * 4 + 2] = byte(colors[i * 3 + 2]);
    // Opacity is a LOGIT in the lift's output, as it is on the GPU path.
    colour[i * 4 + 3] = byte(sigmoid(opacities[i]));
  }
}

/** A ready-to-draw `GaussianSplat` geometry for one set of SoA Gaussians. */
export function splatGeometryFromGaussians(g: Gaussians): THREE.BufferGeometry {
  const centre = new Float32Array(g.count * 3);
  const cov = new Float32Array(g.count * 6);
  const colour = new Uint8ClampedArray(g.count * 4);
  writeSplatAttributes(g, g.count, centre, cov, colour);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(centre, 3));
  geo.setAttribute("covariance", new THREE.BufferAttribute(cov, 6));
  geo.setAttribute("color", new THREE.BufferAttribute(colour, 4, true));
  geo.computeBoundingSphere();
  return geo;
}

export class SplatMeshUpdater {
  private mesh?: THREE.Object3D & { geometry: THREE.BufferGeometry };

  /** The built mesh, for a reader that needs its world placement — the PBR shade
   *  lights in world space and the lift works in the training frame. Undefined
   *  until the first update has built it. */
  get splatMesh(): (THREE.Object3D & { geometry: THREE.BufferGeometry }) | undefined {
    return this.mesh;
  }
  /** Optional cap on splat count. */
  splatCap: number | null = null;
  /** Uniform scale applied to the splat (cm→m worldScale). The lift emits
   *  positions in the training cm frame; scaling the mesh maps it into the
   *  scene's meter frame. 1 = render verbatim. */
  meshScale = 1;
  /** Scene-space placement (metres), applied AFTER meshScale. */
  meshOffset: [number, number, number] = [0, 0, 0];
  /** scene.json world_rotation as a quaternion — the training frame's own
   *  orientation, undone here so the avatar renders upright. */
  meshRotation: THREE.Quaternion | null = null;

  // Update coalescing — a lift can land faster than the mesh can absorb it, so
  // only the latest pending set is kept and they are applied serially.
  private pending: Gaussians | null = null;
  private inFlight = false;
  private disposed = false;
  private visible = true;
  /** Fixed for the session, like the GPU path's slot layout: the mesh is built
   *  once at the first update's count and later updates write into it. A LARGER
   *  set rebuilds; the CPU lift's count is stable in practice, so that is a
   *  safety valve rather than a per-frame path. */
  private capacity = 0;
  private centre: Float32Array | null = null;
  private cov: Float32Array | null = null;
  private colour: Uint8ClampedArray | null = null;

  private parent: THREE.Object3D;

  // Explicit assignment, NOT a TS parameter property: node's strip-only TS mode
  // rejects those, and tests/unit/splatMeshUpdaterOffset loads this module.
  constructor(parent: THREE.Object3D) {
    this.parent = parent;
  }

  /** Show/hide the splat without tearing it down (blueprint renderer-node gate). */
  setVisible(v: boolean) {
    this.visible = v;
    if (this.mesh) this.mesh.visible = v;
  }

  /** Queue an update from the concatenated Gaussians (latest wins). */
  updateGaussians(g: Gaussians) {
    if (this.disposed) return;
    if (g.packed) {
      throw new Error("[isaac] SplatMeshUpdater takes SoA Gaussians; a packed lifter writes three's buffers directly (setThreeTargets)");
    }
    // splatCap caps the prefix (contiguous SoA). Snapshot: the lifter's arrays
    // are reused on the next lift and the apply below is async.
    const N = Math.min(g.count, this.splatCap ?? g.count);
    this.pending = {
      count: N,
      positions: new Float32Array(g.positions.subarray(0, N * 3)),
      scales: new Float32Array(g.scales.subarray(0, N * 3)),
      rotations: new Float32Array(g.rotations.subarray(0, N * 4)),
      colors: new Float32Array(g.colors.subarray(0, N * 3)),
      opacities: new Float32Array(g.opacities.subarray(0, N)),
    };
    if (this.inFlight) return;
    this.runUpdateLoop().catch((e) => console.warn("[isaac] splat update loop error", e));
  }

  private async runUpdateLoop() {
    this.inFlight = true;
    try {
      while (this.pending) {
        const next = this.pending;
        this.pending = null;
        this.apply(next);
        if (this.disposed) return;
        // Yield so a burst of lifts cannot starve the frame; the loop picks up
        // whatever the newest pending set is.
        await Promise.resolve();
      }
    } finally {
      this.inFlight = false;
    }
  }

  private ensureMesh(count: number) {
    if (this.mesh && count <= this.capacity) return;
    if (this.mesh) {
      this.parent.remove(this.mesh);
      // A REAL dispose since `GaussianSplatPatched.js` grew one (PATCHED (aos)).
      // Upstream `GaussianSplat` defines none, so this call — and the one in
      // `dispose` below — were optional-chaining over nothing, and every growth
      // in capacity left the previous mesh's geometry, material and source
      // buffers alive.
      (this.mesh as unknown as { dispose?: () => void }).dispose?.();
      this.mesh = undefined;
    }
    this.capacity = count;
    this.centre = new Float32Array(count * 3);
    this.cov = new Float32Array(count * 6);
    this.colour = new Uint8ClampedArray(count * 4);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.centre, 3));
    geo.setAttribute("covariance", new THREE.BufferAttribute(this.cov, 6));
    geo.setAttribute("color", new THREE.BufferAttribute(this.colour, 4, true));
    const mesh = new GaussianSplat(geo, { autoSort: true });
    // The lift MOVES splats every pose, so a sort invalidated by camera
    // direction alone is not enough — three's own `autoSort` re-sorts on a
    // camera move, and `markSplatsChanged` below forces one when the data moved.
    mesh.frustumCulled = false;
    applyMeshPlacement(mesh, this.meshScale, this.meshOffset, this.meshRotation);
    mesh.visible = this.visible;
    this.mesh = mesh as unknown as THREE.Object3D & { geometry: THREE.BufferGeometry };
    this.parent.add(mesh);
  }

  /** SoA Gaussians → three's attributes, in place. */
  private apply(g: Gaussians) {
    this.ensureMesh(g.count);
    const centre = this.centre!, cov = this.cov!, colour = this.colour!;
    writeSplatAttributes(g, g.count, centre, cov, colour);
    // A shorter set leaves the tail from the previous update behind, so it is
    // cleared to transparent rather than left as stale splats — the same rule
    // the GPU path's inactive-branch slots follow.
    for (let i = g.count; i < this.capacity; i++) colour[i * 4 + 3] = 0;
    const geo = this.mesh!.geometry;
    for (const name of ["position", "covariance", "color"]) {
      const attr = geo.getAttribute(name) as THREE.BufferAttribute | undefined;
      if (attr) attr.needsUpdate = true;
    }
    // The centres MOVED, so the depth order is stale in a way a camera-direction
    // test cannot see. Without this the mesh keeps the previous pose's order and
    // composites a different set of splats front-to-back — the same hazard the
    // GPU path documents in sortPolicy.
    (this.mesh as unknown as { markSplatsChanged?: () => void }).markSplatsChanged?.();
    geo.computeBoundingSphere();
  }

  /** Remove the splat from the scene and release it. Safe to call twice. */
  dispose() {
    this.disposed = true;
    this.pending = null;
    if (this.mesh) {
      this.parent.remove(this.mesh);
      (this.mesh as unknown as { dispose?: () => void }).dispose?.();
      this.mesh = undefined;
    }
    this.centre = this.cov = null;
    this.colour = null;
  }
}
